import { londonNow } from "@package/grid-utils";
import { useGraphQLClient } from "@package/graphql/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { pagesQueryOptions, useUpdatePage } from "../page";
import { withoutExpiredAdhocSpreads } from "./expired";
import { isValidSpread, SpreadJSON, SpreadRecordFromJSON, type TAdhocSpreadFormatted } from "./types";
import { useUserId } from "../../../context/auth/hooks";

export const useAdhocSpreads = (pageId: string) => {
  const hasura = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    ...pagesQueryOptions(hasura, useQueryClient(), userId),
    select: (x): TAdhocSpreadFormatted => {
      const page = x.find((y) => y.id === pageId);
      return SpreadRecordFromJSON.safeParse(page?.adhoc_spreads).data ?? {};
    },
  });
};

/**
 * Valid adhoc spreads whose periods are still on the sheet.
 * A spread with an expired leg would render blank, so that row is omitted.
 */
export const useValidAdhocSpreads = (pageId: string) => {
  const hasura = useGraphQLClient();
  const userId = useUserId();

  return useQuery({
    ...pagesQueryOptions(hasura, useQueryClient(), userId),
    select: (x) => {
      const page = x.find((y) => y.id === pageId);
      const spreads = SpreadJSON.safeParse(page?.adhoc_spreads).data?.filter(isValidSpread) ?? [];
      return withoutExpiredAdhocSpreads(spreads, londonNow());
    },
  });
};

export const useUpdateAdhocSpreads = () => {
  const updatePage = useUpdatePage();
  const update = useCallback(
    (data: { id: string; adhoc_spreads: TAdhocSpreadFormatted }) => {
      return updatePage.optimistic({
        ...data,
        adhoc_spreads: SpreadRecordFromJSON.encode(data.adhoc_spreads),
      });
    },
    [updatePage],
  );

  return update;
};
